<template>
  <div>
    <!-- Filter Section -->
    <div class="filter-section">
      <a-row :gutter="16">
        <a-col :span="8">
          <p style="margin: 0px">Search</p>
          <a-input-search
            v-model="search"
            placeholder="Search by Invoice #"
            @search="onSearch"
            allow-clear
            style="width: 100%"
          >
            <a-icon slot="prefix" type="search" />
          </a-input-search>
        </a-col>
        <!-- <a-col :span="6">
          <p style="margin: 0px">Filter by: Status</p>
          <a-select
            v-model="filters.status"
            placeholder="All"
            allowClear
            style="width: 100%"
          >
            <a-select-option value="">All</a-select-option>
            <a-select-option value="Confirmed">Confirmed</a-select-option>
            <a-select-option value="Pending">Pending</a-select-option>
            <a-select-option value="Repaid">Repaid</a-select-option>
          </a-select>
        </a-col> -->
        <a-col :span="10">
          <p style="margin: 0px">Date Range</p>
          <a-range-picker
            v-model="filters.dateRange"
            placeholder="['Start Date', 'End Date']"
            style="width: 100%"
            format="YYYY-MM-DD"
            :allowClear="true"
          />
        </a-col>
      </a-row>
    </div>

    <!-- Repayment Requests Table -->
    <div class="table-section">
      <div
        style="
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        "
      >
        <h3 style="margin: 0">Repayment Requests</h3>
      </div>
      <a-table
        :columns="requestColumns"
        :data-source="tableData"
        row-key="id"
        :pagination="tablePagination"
        :scroll="{ x: 'max-content' }"
        :loading="tableLoading"
        @change="handleTableChange"
      >
        <template slot="totalValue" slot-scope="text">
          {{ formatAmount(text) }}
        </template>
        <template slot="outstanding" slot-scope="text">
          {{ formatAmount(text) }}
        </template>
        <template slot="status" slot-scope="text">
          <StatusTag :status="text" />
        </template>
        <template slot="actions" slot-scope="text, record">
          <a-button
            type="link"
            icon="eye"
            style="cursor: pointer; color: #26a246"
            @click="viewRepaymentDetails(record)"
          >
            View
            </a-button>
        </template>
      </a-table>
    </div>


    <!-- Repayment Details Drawer -->
    <RepaymentDetailsDrawer
      ref="repaymentDetailsDrawer"
      :visible="showRepaymentDetailsDrawer"
      :repaymentData="selectedRepayment"
      @close="showRepaymentDetailsDrawer = false"
      @repayment-success="onRepaymentSuccess"
    />
  </div>
</template>

<script>
import StatusTag from "../shared-components/StatusTag.vue";
import RepaymentDetailsDrawer from "./RepaymentDetailsDrawer.vue";
import { formatDate, formatAmount } from "../../utils";
import FinancingAndPaymentsService from "../../api/financing-and-payments.service";

export default {
  name: "RepaymentRequestsTab",
  components: {
    StatusTag,
    RepaymentDetailsDrawer,
  },
  data() {
    return {
      // Search and filters
      search: "",
      searchDebounceTimer: null,
      filters: {
        status: null,
        dateRange: null,
      },

      // Pagination
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        totalCount: 0,
      },

      // Table data and loading
      tableData: [],
      tableLoading: false,
      
      // Drawer state
      showRepaymentDetailsDrawer: false,
      selectedRepayment: null,
      requestColumns: [
        {
          title: "Invoice ID",
          dataIndex: "invoiceIdFormatted",
          key: "invoiceIdFormatted",
          width: 120,
        },
        {
          title: "Buyer Name",
          dataIndex: "buyerName",
          key: "buyerName",
          width: 150,
        },
        {
          title: "Amount",
          dataIndex: "totalValue",
          key: "totalValue",
          scopedSlots: { customRender: "totalValue" },
          width: 120,
        },
        {
          title: "Outstanding",
          dataIndex: "outstanding",
          key: "outstanding",
          scopedSlots: { customRender: "outstanding" },
          width: 120,
        },
        {
          title: "Status",
          dataIndex: "repaymentStatus",
          key: "repaymentStatus",
          scopedSlots: { customRender: "status" },
          width: 120,
        },
        {
          title: "Actions",
          key: "actions",
          scopedSlots: { customRender: "actions" },
          width: 100,
          fixed: "right",
        },
      ],
    };
  },
  mounted() {
    this.fetchData();
  },
  computed: {
    tablePagination() {
      return {
        current: this.pagination.pageIndex,
        pageSize: this.pagination.pageSize,
        total: this.pagination.totalCount,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
        showSizeChanger: false,
      };
    },
  },
  methods: {
    formatAmount,
    formatDate,
    async fetchData() {
      this.tableLoading = true;

      try {
        // Convert dateRange to startDate and endDate
        let startDate = "";
        let endDate = "";

        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          // Create Date objects with time 00:00:00 for start date
          const startDateObj = new Date(
            this.filters.dateRange[0].format("YYYY-MM-DD")
          );
          startDateObj.setHours(0, 0, 0, 0);
          startDate = startDateObj;

          // Create Date objects with time 23:59:59 for end date
          const endDateObj = new Date(
            this.filters.dateRange[1].format("YYYY-MM-DD")
          );
          endDateObj.setHours(23, 59, 59, 999);
          endDate = endDateObj;
        }

        const params = {
          pageIndex: this.pagination.pageIndex,
          pageSize: this.pagination.pageSize,
          status: this.filters.status || "",
          search: this.search || "",
          startDate: startDate,
          endDate: endDate,
        };

        const response = await FinancingAndPaymentsService.getEligibleInvoicesForRepayment(
          params
        );

        if (response.data?.code === 200) {
          const data = response.data.data;
          this.tableData = (data.results || []).map((item) => ({
            id: item.invoiceId,
            invoiceId: item.invoiceId,
            invoiceIdFormatted: item.invoiceIdFormatted,
            buyerName: item.buyerName,
            sellerName: item.sellerName,
            invoiceDate: item.invoiceDate,
            totalValue: item.factoredValue,
            dispensedSoFar: item.dispensedSoFar,
            repaid: item.repaid,
            outstanding: item.outstanding,
            invoiceAge: item.invoiceAge,
            repaymentStatus: item.repaymentStatus,
            factoringStatus: item.factoringStatus,
            disbursementStatus: item.disbursementStatus,
            repaymentDueDate: item.repaymentDueDate,
          }));
          this.pagination.totalCount = data.totalCount || 0;
        } else {
          this.tableData = [];
          this.pagination.totalCount = 0;
          this.$message.warning("No repayment data received from the server");
        }
      } catch (error) {
        console.error("Error fetching repayments:", error);
        this.tableData = [];
        this.pagination.totalCount = 0;
        this.$message.error("Failed to fetch repayments. Please try again.");
      } finally {
        this.tableLoading = false;
      }
    },
    onSearch(value) {
      this.search = value;
      this.pagination.pageIndex = 1;
      this.fetchData();
    },
    viewRepaymentDetails(record) {
      this.selectedRepayment = record;
      this.showRepaymentDetailsDrawer = true;
    },
    createRepayment() {
      // This method can be removed or used to open drawer with a default invoice
      console.log('Create repayment - this should now be handled by the drawer');
    },
    onRepaymentSuccess() {
      console.log("Repayment created successfully");
      this.fetchData(); // Refresh the table data
      // Payment history will be refreshed by the drawer itself
    },
    handleTableChange(pagination) {
      if (pagination.current !== this.pagination.pageIndex) {
        this.pagination.pageIndex = pagination.current;
        this.fetchData();
      }
    },
  },
  watch: {
    "pagination.pageIndex"() {
      this.fetchData();
    },
    search() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.pagination.pageIndex = 1;
        this.fetchData();
      }, 400);
    },
    filters: {
      handler() {
        this.pagination.pageIndex = 1;
        this.fetchData();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.filter-section {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.filter-section .ant-row {
  margin-bottom: 0;
}

.filter-section .ant-col {
  margin-bottom: 16px;
}

.filter-section .ant-col:last-child {
  margin-bottom: 0;
}

a-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #374151;
}

.table-section {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
}

.table-section h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

/* Responsive */
@media (max-width: 768px) {
  .filter-section .ant-col {
    margin-bottom: 16px;
  }
}
</style>
